import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { mockData } from '../mock';

const Contact = ({ currentLang }) => {
  const t = mockData.translations[currentLang];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Message sent successfully! We will get back to you soon. (Mock functionality)');
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div className="bg-black min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="px-[7.6923%]">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Contact <span className="text-[#00FFD1]">Us</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed">
              Ready to explore investment opportunities? Get in touch with our team
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">Get In Touch</h2>
              
              {/* Office Address */}
              <div className="bg-white/5 border border-white/20 p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#00FFD1]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Jakarta Office</h3>
                    <p className="text-white/85 leading-relaxed">
                      {mockData.company.address}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Contact Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#00FFD1]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Email</p>
                    <a href={`mailto:${mockData.company.email}`} className="text-white text-lg hover:text-[#00FFD1] transition-colors">
                      {mockData.company.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#00FFD1]/20 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#00FFD1]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">WhatsApp</p>
                    <a href={`https://wa.me/${mockData.company.whatsapp.replace(/[^0-9]/g, '')}`} className="text-white text-lg hover:text-[#00FFD1] transition-colors">
                      {mockData.company.whatsapp}
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Office Image */}
              <div className="mt-8">
                <img 
                  src={mockData.images.office1}
                  alt="Office Interior"
                  className="w-full h-64 object-cover border border-white/20"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">Send a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/10 border border-white/25 text-white placeholder:text-white/50 focus:border-[#00FFD1] focus:ring-[#00FFD1] rounded-none"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/10 border border-white/25 text-white placeholder:text-white/50 focus:border-[#00FFD1] focus:ring-[#00FFD1] rounded-none"
                    placeholder="your.email@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Company</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full bg-white/10 border border-white/25 text-white placeholder:text-white/50 focus:border-[#00FFD1] focus:ring-[#00FFD1] rounded-none"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full bg-white/10 border border-white/25 text-white placeholder:text-white/50 focus:border-[#00FFD1] focus:ring-[#00FFD1] rounded-none resize-none"
                    placeholder="Tell us about your investment interests or partnership opportunities..."
                  />
                </div>
                
                <Button
                  type="submit"
                  className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/90 border-none rounded-none py-4 px-8 text-lg font-medium min-h-[56px] w-full transition-all duration-400"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>
              
              <div className="mt-8 p-6 bg-white/5 border border-white/20">
                <h4 className="text-white font-semibold mb-3">Investment Inquiries</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  For investment opportunities, partnership discussions, or investor relations inquiries, 
                  please include details about your investment size, timeline, and areas of interest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 border-t border-white/25">
        <div className="px-[7.6923%]">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Location</h2>
            <p className="text-xl text-white/70">
              Visit us at our Jakarta headquarters
            </p>
          </div>
          
          {/* Jakarta Skyline Image as Map Placeholder */}
          <div className="relative">
            <img 
              src={mockData.images.jakartaSkyline}
              alt="Jakarta Skyline"
              className="w-full h-96 object-cover border border-white/20"
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="text-center text-white">
                <MapPin className="w-12 h-12 mx-auto mb-2 text-[#00FFD1]" />
                <p className="text-lg font-semibold">Nusantara Capital.ai</p>
                <p className="text-sm text-white/70">South Jakarta, Indonesia</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;